import React from 'react';
import { twMerge } from 'tailwind-merge';
import Checkbox from './ui-components/Checkbox';
import CodeBlockWithCopy from './CodeBlockWithCopy';
import {
    processShortEmojiMessage,
    isShortEmojiMessage,
} from 'flow/utils/message-utils';
import { processMarkdownWithMentions } from 'flow/utils/markdown-with-mentions';
import { TypedMap } from 'flow/types/TypedMap';
import { Message } from 'flow/types/Chats';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import useFeatureFlags, { FeatureFlags } from 'flow/hooks/useFeatureFlags';

interface Props {
    message: TypedMap<Message>;
    sessionKey: ChatSessionKey;
    /** Dark variant, see MessagesPage. */
    videoDark?: boolean;
}

// Component definitions moved outside to avoid recreation on each render
const CodeComponent = ({ children, ...props }: any) => {
    const codeString = String(children).replace(/\n$/, '');
    return <CodeBlockWithCopy code={codeString} inline={!!props.inline} />;
};

const RICKROLL_URL = 'https://www.youtube.com/watch?v=dQw4w9WgXcQ';

const getLinkComponent =
    (videoDark: boolean) =>
    ({ children, ...props }: any) => (
        <a
            {...props}
            target="_blank"
            rel="noopener noreferrer"
            className={videoDark ? '!text-blue-300' : undefined}
        >
            {children}
        </a>
    );

const RickrollLinkComponent = ({ children, ...props }: any) => (
    <a {...props} href={RICKROLL_URL} target="_blank" rel="noopener noreferrer">
        {children}
    </a>
);

const getParagraphComponent =
    (videoDark: boolean) =>
    ({ children }: any) => (
        <p
            className={twMerge(
                'leading-6 whitespace-pre-wrap',
                videoDark ? 'text-gray-300' : 'text-black',
            )}
        >
            {children}
        </p>
    );

const InputComponent = ({ checked, type, ...props }: any) => {
    if (type === 'checkbox') {
        return (
            <Checkbox
                checked={!!checked}
                disabled={true}
                onChange={() => {}}
                className="inline-block align-middle"
            />
        );
    }
    return <input type={type} checked={checked} {...props} />;
};

const getMarkdownComponents = (videoDark: boolean) => ({
    code: CodeComponent,
    a: getLinkComponent(videoDark),
    p: getParagraphComponent(videoDark),
    input: InputComponent,
});

const StyledMessageBody = ({
    message,
    sessionKey,
    videoDark = false,
}: Props) => {
    const msg = message.get('message');
    const stylings = message.get('stylings');
    const { hasFeature } = useFeatureFlags();

    const hasMarkdownEnabled = hasFeature(FeatureFlags.MARKDOWN) || false;
    const hasEasterEggs = hasFeature(FeatureFlags.EASTER_EGGS) || false;

    const markdownComponents = getMarkdownComponents(videoDark);
    const components = hasEasterEggs
        ? { ...markdownComponents, a: RickrollLinkComponent }
        : markdownComponents;

    if (!msg) return '';

    // Check if this is a short emoji message for special handling
    const isShortEmoji = isShortEmojiMessage(msg);

    // Process markdown with mentions only if feature is enabled
    const processedContent = processMarkdownWithMentions(
        msg,
        stylings ? stylings.toArray() : [],
        sessionKey,
        components,
        hasMarkdownEnabled,
    );

    // Apply emoji processing if needed BEFORE wrapping in div
    const finalContent = isShortEmoji
        ? processShortEmojiMessage(
              message?.get('message'),
              message?.get('key'),
              processedContent,
          )
        : processedContent;

    return (
        <div className="markdown max-w-none whitespace-break-spaces">
            {finalContent}
        </div>
    );
};

export default StyledMessageBody;
